import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import DashboardLayout from "../layouts/DashboardLayout";
import { AuthContext } from "../context/AuthContext";
import api from "../api/client";
import { INCIDENT_TYPES, SOURCE_OPTIONS } from "../constants/incidentOptions";
import { useAnnouncement } from "../context/AnnouncementContext";
import { useMaintenance } from "../context/MaintenanceContext";
// Support tickets removed
import {
  DEFAULT_PING_INTERVAL_SECONDS,
  DEFAULT_STATUS_CHECK_MINUTES,
  MAX_PING_INTERVAL_SECONDS,
  MIN_PING_INTERVAL_SECONDS,
} from "../constants/systemConfig";

const formatValue = (value) => {
  if (value === null || value === undefined) {
    return "Not provided";
  }

  const stringValue = typeof value === "string" ? value.trim() : String(value);

  if (stringValue.length === 0) {
    return "Not provided";
  }

  return stringValue;
};

// Ticket status constants removed

const formatTimestamp = (iso) => {
  if (!iso) {
    return "";
  }
  try {
    return new Date(iso).toLocaleString();
  } catch (error) {
    return iso;
  }
};

function AdminPanel() {
  const { user, refreshUser, login } = useContext(AuthContext);
  const isAdmin = (user?.role ?? "").toLowerCase() === "admin";
  const { announcement, updateAnnouncement, clearAnnouncement } = useAnnouncement();
  const { maintenanceMode, maintenanceMessage, setMaintenanceMode } = useMaintenance();

  const [maintenanceForm, setMaintenanceForm] = useState({
    enabled: maintenanceMode,
    message: maintenanceMessage || '',
  });
  const [maintenanceAlert, setMaintenanceAlert] = useState(null);

  // Sync maintenance form with context
  useEffect(() => {
    setMaintenanceForm({
      enabled: maintenanceMode,
      message: maintenanceMessage || '',
    });
  }, [maintenanceMode, maintenanceMessage]);

  const [formValues, setFormValues] = useState({
    name: user?.name ?? "",
    email: user?.email ?? "",
    phoneNumber: user?.phoneNumber ?? "",
    rangerId: user?.rangerId ?? "",
    division: user?.division ?? "",
  });

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [adminUsers, setAdminUsers] = useState([]);
  const [adminUsersLoading, setAdminUsersLoading] = useState(false);
  const [adminUsersError, setAdminUsersError] = useState("");
  const [adminAlert, setAdminAlert] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    rangerId: "",
    division: "",
    canViewComms: false,
  });
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState("");
  const [statusTimerForm, setStatusTimerForm] = useState({
    defaultMinutes: String(DEFAULT_STATUS_CHECK_MINUTES),
    overrides: {},
    pingIntervalSeconds: String(DEFAULT_PING_INTERVAL_SECONDS),
  });
  const [statusTimerAlert, setStatusTimerAlert] = useState(null);
  const [statusTimerLoading, setStatusTimerLoading] = useState(false);
  const [statusTimerSaving, setStatusTimerSaving] = useState(false);
  // Support tickets state removed

  const createFormTemplate = useMemo(
    () => ({
      username: "",
      password: "",
      role: "ranger",
      name: "",
      email: "",
      phoneNumber: "",
      rangerId: "",
      division: "",
      canViewComms: false,
      incidentCreationRestricted: false,
    }),
    []
  );
  const [createForm, setCreateForm] = useState(() => ({ ...createFormTemplate }));
  const [createLoading, setCreateLoading] = useState(false);
  const roleOptions = useMemo(() => ["ranger", "supervisor", "admin"], []);
  const resetCreateForm = useCallback(() => setCreateForm({ ...createFormTemplate }), [createFormTemplate]);
  const presetAnnouncements = useMemo(
    () => [
      {
        label: "Scheduled Maintenance",
        message: "Maintenance window tonight at 23:00. Expect brief downtime.",
      },
      {
        label: "System Update",
        message: "A new RangerVault update will deploy shortly. Please save your work.",
      },
      {
        label: "Training Reminder",
        message: "Mandatory quarterly training is due by Friday. See RangerAcademy for details.",
      },
    ],
    []
  );
  const [announcementForm, setAnnouncementForm] = useState({
    message: announcement?.message ?? "",
    type: announcement?.type ?? "general",
    beaconsEnabled: announcement?.beaconsEnabled ?? false,
  });
  const [announcementAlert, setAnnouncementAlert] = useState(null);

  // Patch Notes / Updates Announcements (database-backed)
  const [patchNotes, setPatchNotes] = useState([]);
  const [patchNotesLoading, setPatchNotesLoading] = useState(false);
  const [patchNotesAlert, setPatchNotesAlert] = useState(null);
  const [patchNoteForm, setPatchNoteForm] = useState({
    title: "",
    content: "",
    type: "update",
    version: "",
    showAsModal: true,
    expiresAt: "",
  });
  const [patchNoteFormMode, setPatchNoteFormMode] = useState("create"); // "create" or "edit"
  const [editingPatchNoteId, setEditingPatchNoteId] = useState(null);
  const [patchNoteSaving, setPatchNoteSaving] = useState(false);

  // Resource Management (Quick Links, Policies, ICS-205)
  const [resources, setResources] = useState([]);
  const [resourcesLoading, setResourcesLoading] = useState(false);
  const [resourcesAlert, setResourcesAlert] = useState(null);

  // Certification Review
  const [pendingCerts, setPendingCerts] = useState([]);
  const [pendingCertsLoading, setPendingCertsLoading] = useState(false);
  const [pendingCertsAlert, setPendingCertsAlert] = useState(null);
  const [rejectDialog, setRejectDialog] = useState({ open: false, certId: null, notes: "" });
  const [reviewLoading, setReviewLoading] = useState(null); // certId currently being reviewed

  const [resourceForm, setResourceForm] = useState({
    type: "quick-link",
    title: "",
    description: "",
    url: "",
    category: "common",
    content: "",
    sortOrder: 0,
    isActive: true,
  });
  const [resourceFormMode, setResourceFormMode] = useState("create");
  const [editingResourceId, setEditingResourceId] = useState(null);
  const [resourceSaving, setResourceSaving] = useState(false);
  const [resourceTypeFilter, setResourceTypeFilter] = useState("all");

  // ICS-205 Editor State
  const [ics205Form, setIcs205Form] = useState({
    incidentName: "Texas Highway Rangers - Statewide Operations",
    operationalPeriod: {
      dateFrom: new Date().toLocaleDateString(),
      dateTo: new Date().toLocaleDateString(),
      timeFrom: "0600",
      timeTo: "1800",
    },
    basicRadioChannelUse: [
      { zone: "STATEWIDE", channel: "RANGER TAC 1", function: "Primary", assignment: "All Units", rxFreq: "155.4750", rxTone: "CSQ", txFreq: "155.4750", txTone: "CSQ", mode: "A", remarks: "Primary Tactical" },
    ],
    specialInstructions: [
      "All units maintain radio discipline during operations.",
    ],
    preparedBy: {
      name: "Admin",
      position: "Communications Unit Leader",
      date: new Date().toLocaleDateString(),
      time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
    },
  });
  const [ics205ResourceId, setIcs205ResourceId] = useState(null);

  // Health Dashboard State
  const [healthSummary, setHealthSummary] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [healthError, setHealthError] = useState("");

  // Active Sessions State (online users)
  const [activeSessions, setActiveSessions] = useState([]);
  const [activeSessionsLoading, setActiveSessionsLoading] = useState(false);

  // User Modals State (targeted alerts for specific users)
  const [userModals, setUserModals] = useState([]);
  const [userModalsLoading, setUserModalsLoading] = useState(false);
  const [userModalsAlert, setUserModalsAlert] = useState(null);
  const [userModalForm, setUserModalForm] = useState({
    targetUserId: "",
    title: "",
    message: "",
  });
  const [userModalSaving, setUserModalSaving] = useState(false);

  // Login Background State
  const [loginBackground, setLoginBackground] = useState("loginbackground.png");
  const [loginBackgroundLoading, setLoginBackgroundLoading] = useState(false);
  const [loginBackgroundSaving, setLoginBackgroundSaving] = useState(false);
  const [loginBackgroundAlert, setLoginBackgroundAlert] = useState(null);
  const loginBackgroundOptions = [
    { value: "loginbackground.png", label: "Background 1" },
    { value: "loginbackground2.png", label: "Background 2" },
    { value: "loginbackground3.png", label: "Background 3" },
    { value: "loginbackground4.png", label: "Background 4" },
    { value: "loginbackground5.png", label: "Background 5" },
  ];

  // Video Requirements State
  const [videoReqForm, setVideoReqForm] = useState({
    videoRequiredByDefault: true,
    exemptIncidentTypes: [],
    exemptSources: [],
  });
  const [videoReqLoading, setVideoReqLoading] = useState(false);
  const [videoReqSaving, setVideoReqSaving] = useState(false);
  const [videoReqAlert, setVideoReqAlert] = useState(null);

  const fetchStatusTimers = useCallback(() => {
    if (!isAdmin) {
      return;
    }

    setStatusTimerLoading(true);
    setStatusTimerAlert(null);

    api
      .get("/status-check-settings")
      .then((res) => {
        const { defaultMinutes, overrides, pingIntervalSeconds } = res.data || {};
        const normalizedOverrides = {};

        // Normalize overrides to match INCIDENT_TYPES format (uppercase)
        (overrides || []).forEach((item) => {
          if (item?.incidentType) {
            // Convert to uppercase to match INCIDENT_TYPES constant
            const normalizedType = item.incidentType.toUpperCase();
            // Only include if it's in the current INCIDENT_TYPES list
            if (INCIDENT_TYPES.includes(normalizedType)) {
              normalizedOverrides[normalizedType] = String(item.minutes ?? "");
            }
          }
        });

        setStatusTimerForm({
          defaultMinutes: String(defaultMinutes ?? ""),
          overrides: normalizedOverrides,
          pingIntervalSeconds: String(
            Number.isFinite(pingIntervalSeconds) && pingIntervalSeconds > 0
              ? pingIntervalSeconds
              : DEFAULT_PING_INTERVAL_SECONDS
          ),
        });
      })
      .catch((error) => {
        console.error("Failed to load status check timers", error);
        setStatusTimerAlert({
          severity: "error",
          message: "Failed to load status check timers. Please try again.",
        });
      })
      .finally(() => setStatusTimerLoading(false));
  }, [isAdmin]);

  // refreshTickets removed

  // Patch Notes / Updates fetch - must be defined before useEffect that uses it
  const fetchPatchNotes = useCallback(async () => {
    setPatchNotesLoading(true);
    setPatchNotesAlert(null);
    try {
      const res = await api.get("/announcements/all");
      setPatchNotes(res.data || []);
    } catch (err) {
      console.error("Failed to load patch notes:", err);
      setPatchNotesAlert({ severity: "error", message: "Failed to load announcements." });
    } finally {
      setPatchNotesLoading(false);
    }
  }, []);

  // Resources fetch (Quick Links, Policies, ICS-205)
  const fetchResources = useCallback(async () => {
    setResourcesLoading(true);
    setResourcesAlert(null);
    try {
      const res = await api.get("/resources?includeInactive=true");
      const allResources = res.data || [];
      setResources(allResources);
      
      // Load ICS-205 data into editor if it exists
      const ics205Resource = allResources.find(r => r.type === "ics-205");
      if (ics205Resource) {
        setIcs205ResourceId(ics205Resource.id);
        if (ics205Resource.content) {
          try {
            const parsedIcs205 = JSON.parse(ics205Resource.content);
            setIcs205Form(parsedIcs205);
          } catch (e) {
            console.error("Failed to parse ICS-205 data:", e);
          }
        }
      }
    } catch (err) {
      console.error("Failed to load resources:", err);
      setResourcesAlert({ severity: "error", message: "Failed to load resources." });
    } finally {
      setResourcesLoading(false);
    }
  }, []);

  // Health Dashboard fetch
  const fetchHealthSummary = useCallback(async () => {
    if (!isAdmin) return;
    setHealthLoading(true);
    setHealthError("");
    try {
      const res = await api.get("/health/summary");
      setHealthSummary(res.data);
    } catch (err) {
      console.error("Failed to load health summary:", err);
      setHealthError("Unable to load health summary.");
    } finally {
      setHealthLoading(false);
    }
  }, [isAdmin]);

  // Active Sessions fetch (online users with client info)
  const fetchActiveSessions = useCallback(async () => {
    if (!isAdmin) return;
    setActiveSessionsLoading(true);
    try {
      const res = await api.get("/status/active-sessions");
      setActiveSessions(res.data || []);
    } catch (err) {
      console.error("Failed to load active sessions:", err);
    } finally {
      setActiveSessionsLoading(false);
    }
  }, [isAdmin]);

  // User Modals fetch (targeted user alerts)
  const fetchUserModals = useCallback(async () => {
    if (!isAdmin) return;
    setUserModalsLoading(true);
    setUserModalsAlert(null);
    try {
      const res = await api.get("/usermodals");
      setUserModals(res.data || []);
    } catch (err) {
      console.error("Failed to load user modals:", err);
      setUserModalsAlert({ severity: "error", message: "Failed to load user modals." });
    } finally {
      setUserModalsLoading(false);
    }
  }, [isAdmin]);

  const handleUserModalFormChange = (field) => (event) => {
    const { value } = event.target;
    setUserModalForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreateUserModal = async (event) => {
    event.preventDefault();
    if (!userModalForm.targetUserId || !userModalForm.title || !userModalForm.message) {
      setUserModalsAlert({ severity: "error", message: "Please fill in all fields." });
      return;
    }

    setUserModalSaving(true);
    setUserModalsAlert(null);
    try {
      await api.post("/usermodals", {
        targetUserId: Number(userModalForm.targetUserId),
        title: userModalForm.title,
        message: userModalForm.message,
      });
      setUserModalsAlert({ severity: "success", message: "User modal created successfully." });
      setUserModalForm({ targetUserId: "", title: "", message: "" });
      fetchUserModals();
    } catch (err) {
      console.error("Failed to create user modal:", err);
      const message = err.response?.data?.message || err.response?.data || "Failed to create user modal.";
      setUserModalsAlert({ severity: "error", message: typeof message === "string" ? message : "Failed to create user modal." });
    } finally {
      setUserModalSaving(false);
    }
  };

  const handleDeactivateUserModal = async (modalId) => {
    try {
      await api.put(`/usermodals/${modalId}/deactivate`);
      setUserModalsAlert({ severity: "success", message: "Modal deactivated." });
      fetchUserModals();
    } catch (err) {
      console.error("Failed to deactivate modal:", err);
      setUserModalsAlert({ severity: "error", message: "Failed to deactivate modal." });
    }
  };

  const handleDeleteUserModal = async (modalId) => {
    try {
      await api.delete(`/usermodals/${modalId}`);
      setUserModalsAlert({ severity: "success", message: "Modal deleted." });
      fetchUserModals();
    } catch (err) {
      console.error("Failed to delete modal:", err);
      setUserModalsAlert({ severity: "error", message: "Failed to delete modal." });
    }
  };

  // Login Background Functions
  const fetchLoginBackground = useCallback(async () => {
    if (!isAdmin) return;
    setLoginBackgroundLoading(true);
    try {
      const res = await api.get("/system-settings/login-background");
      setLoginBackground(res.data?.imageFile || "loginbackground.png");
    } catch (err) {
      console.error("Failed to load login background:", err);
      setLoginBackground("loginbackground.png");
    } finally {
      setLoginBackgroundLoading(false);
    }
  }, [isAdmin]);

  const handleLoginBackgroundChange = async (imageFile) => {
    setLoginBackgroundSaving(true);
    setLoginBackgroundAlert(null);
    try {
      await api.put("/system-settings/login-background", { imageFile });
      setLoginBackground(imageFile);
      setLoginBackgroundAlert({ severity: "success", message: "Login background updated successfully." });
    } catch (err) {
      console.error("Failed to update login background:", err);
      const message = err.response?.data || "Failed to update login background.";
      setLoginBackgroundAlert({ severity: "error", message: typeof message === "string" ? message : "Failed to update." });
    } finally {
      setLoginBackgroundSaving(false);
    }
  };

  // Video Requirements Functions
  const fetchVideoRequirements = useCallback(async () => {
    if (!isAdmin) return;
    setVideoReqLoading(true);
    try {
      const res = await api.get("/system-settings/video-requirements");
      setVideoReqForm({
        videoRequiredByDefault: res.data?.videoRequiredByDefault ?? true,
        exemptIncidentTypes: res.data?.exemptIncidentTypes ?? [],
        exemptSources: res.data?.exemptSources ?? [],
      });
    } catch (err) {
      console.error("Failed to load video requirements:", err);
    } finally {
      setVideoReqLoading(false);
    }
  }, [isAdmin]);

  const handleVideoReqToggleType = (type) => {
    const upper = type.toUpperCase();
    setVideoReqForm((prev) => {
      const current = prev.exemptIncidentTypes || [];
      const next = current.includes(upper)
        ? current.filter((t) => t !== upper)
        : [...current, upper];
      return { ...prev, exemptIncidentTypes: next };
    });
  };

  const handleVideoReqToggleSource = (source) => {
    const upper = source.toUpperCase();
    setVideoReqForm((prev) => {
      const current = prev.exemptSources || [];
      const next = current.includes(upper)
        ? current.filter((s) => s !== upper)
        : [...current, upper];
      return { ...prev, exemptSources: next };
    });
  };

  const handleVideoReqSave = async (event) => {
    event.preventDefault();
    setVideoReqSaving(true);
    setVideoReqAlert(null);
    try {
      await api.put("/system-settings/video-requirements", videoReqForm);
      setVideoReqAlert({ severity: "success", message: "Video requirements updated." });
    } catch (err) {
      console.error("Failed to save video requirements:", err);
      const msg = err.response?.data?.message || "Failed to save video requirements.";
      setVideoReqAlert({ severity: "error", message: typeof msg === "string" ? msg : "Save failed." });
    } finally {
      setVideoReqSaving(false);
    }
  };

  const handleStatusTimerDefaultChange = (event) => {
    const { value } = event.target;
    setStatusTimerForm((prev) => ({ ...prev, defaultMinutes: value }));
  };

  const handleStatusTimerOverrideChange = (incidentType) => (event) => {
    const { value } = event.target;
    setStatusTimerForm((prev) => ({
      ...prev,
      overrides: { ...prev.overrides, [incidentType]: value },
    }));
  };

  const handlePingIntervalChange = (event) => {
    const { value } = event.target;
    setStatusTimerForm((prev) => ({ ...prev, pingIntervalSeconds: value }));
  };

  const handleStatusTimerSave = async (event) => {
    event.preventDefault();

    const defaultMinutes = Number(statusTimerForm.defaultMinutes);
    if (!Number.isFinite(defaultMinutes) || defaultMinutes <= 0) {
      setStatusTimerAlert({
        severity: "error",
        message: "Default minutes must be a positive number.",
      });
      return;
    }

    const pingIntervalSeconds = Number(statusTimerForm.pingIntervalSeconds);
    if (
      !Number.isFinite(pingIntervalSeconds) ||
      pingIntervalSeconds < MIN_PING_INTERVAL_SECONDS ||
      pingIntervalSeconds > MAX_PING_INTERVAL_SECONDS
    ) {
      setStatusTimerAlert({
        severity: "error",
        message: `Ping interval must be between ${MIN_PING_INTERVAL_SECONDS} and ${MAX_PING_INTERVAL_SECONDS} seconds.`,
      });
      return;
    }

    let overridesPayload;

    try {
      overridesPayload = Object.entries(statusTimerForm.overrides)
        .map(([incidentType, value]) => {
          if (!value) {
            return null;
          }

          const minutes = Number(value);
          if (!Number.isFinite(minutes) || minutes <= 0) {
            throw new Error(`Override for ${incidentType} must be a positive number.`);
          }

          return { incidentType, minutes };
        })
        .filter(Boolean);
    } catch (err) {
      setStatusTimerAlert({ severity: "error", message: err.message });
      return;
    }

    setStatusTimerSaving(true);
    setStatusTimerAlert(null);

    try {
      await api.put("/status-check-settings", {
        defaultMinutes,
        overrides: overridesPayload,
        pingIntervalSeconds,
      });

      setStatusTimerAlert({ severity: "success", message: "Status check timers saved." });
      fetchStatusTimers();
    } catch (error) {
      console.error("Failed to save status check timers", error);
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.title ||
        "Failed to save status check timers. Please try again.";
      setStatusTimerAlert({ severity: "error", message });
    } finally {
      setStatusTimerSaving(false);
    }
  };

  const fetchAdminUsers = useCallback(() => {
    setAdminUsersLoading(true);
    setAdminUsersError("");

    api
      .get("/users")
      .then((res) => setAdminUsers(res.data))
      .catch((err) => {
        console.error(err);
        setAdminUsersError("Unable to load users right now.");
      })
      .finally(() => setAdminUsersLoading(false));
  }, []);

  // Support tickets functions removed

  const openEditDialog = (account) => {
    if (!account) {
      return;
    }
    setEditTarget(account);
    setEditForm({
      name: account.name || "",
      email: account.email || "",
      phoneNumber: account.phoneNumber || "",
      rangerId: account.rangerId || "",
      division: account.division || "",
      canViewComms: !!account.canViewComms,
      incidentCreationRestricted: !!account.incidentCreationRestricted,
    });
    setEditError("");
  };

  const closeEditDialog = () => {
    if (!editSaving) {
      setEditTarget(null);
    }
  };

  const handleEditFieldChange = (field) => (event) => {
    const { value } = event.target;
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleEditSubmit = async (event) => {
    event.preventDefault();
    if (!editTarget) {
      return;
    }

    setEditSaving(true);
    setEditError("");

    try {
      const trimmedRangerId = editForm.rangerId?.trim();
      const payload = {
        name: editForm.name,
        email: editForm.email,
        phoneNumber: editForm.phoneNumber,
        division: editForm.division,
        canViewComms: editForm.canViewComms,
        incidentCreationRestricted: editForm.incidentCreationRestricted,
      };
      if (trimmedRangerId) {
        payload.rangerId = trimmedRangerId;
      }
      await api.put(`/users/${editTarget.id}`, {
        ...payload,
      });
      setAdminAlert({ type: "success", message: "Account updated." });
      setEditTarget(null);
      fetchAdminUsers();
    } catch (err) {
      console.error(err);
      const message = err.response?.data ?? "Unable to update this account.";
      setEditError(typeof message === "string" ? message : "Unable to update this account.");
    } finally {
      setEditSaving(false);
    }
  };

  useEffect(() => {
    setFormValues({
      name: user?.name ?? "",
      email: user?.email ?? "",
      phoneNumber: user?.phoneNumber ?? "",
      rangerId: user?.rangerId ?? "",
      division: user?.division ?? "",
    });
  }, [user]);

  useEffect(() => {
    setAnnouncementForm({
      message: announcement?.message ?? "",
      type: announcement?.type ?? "general",
      beaconsEnabled: announcement?.beaconsEnabled ?? false,
    });
  }, [announcement]);

  // Certification review
  const fetchPendingCerts = useCallback(async () => {
    setPendingCertsLoading(true);
    try {
      const res = await api.get("/training-certifications/pending");
      setPendingCerts(res.data || []);
    } catch (err) {
      setPendingCertsAlert({ type: "error", message: "Failed to load pending certifications" });
    } finally {
      setPendingCertsLoading(false);
    }
  }, []);

  const handleApproveCert = useCallback(async (certId) => {
    setReviewLoading(certId);
    try {
      await api.put(`/training-certifications/${certId}/review`, { status: "approved" });
      setPendingCertsAlert({ type: "success", message: "Certification approved" });
      fetchPendingCerts();
    } catch {
      setPendingCertsAlert({ type: "error", message: "Failed to approve certification" });
    } finally {
      setReviewLoading(null);
    }
  }, [fetchPendingCerts]);

  const handleRejectCert = useCallback(async () => {
    const { certId, notes } = rejectDialog;
    if (!certId) return;
    setReviewLoading(certId);
    try {
      await api.put(`/training-certifications/${certId}/review`, { status: "rejected", notes });
      setPendingCertsAlert({ type: "success", message: "Certification rejected" });
      setRejectDialog({ open: false, certId: null, notes: "" });
      fetchPendingCerts();
    } catch {
      setPendingCertsAlert({ type: "error", message: "Failed to reject certification" });
    } finally {
      setReviewLoading(null);
    }
  }, [rejectDialog, fetchPendingCerts]);

  useEffect(() => {
    if (isAdmin) {
      fetchAdminUsers();
      fetchStatusTimers();
      fetchPatchNotes();
      fetchResources();
      fetchActiveSessions();
      fetchUserModals();
      fetchLoginBackground();
      fetchVideoRequirements();
      fetchPendingCerts();
      fetchHealthSummary();
    } else {
      setAdminUsers([]);
      setStatusTimerForm({
        defaultMinutes: String(DEFAULT_STATUS_CHECK_MINUTES),
        overrides: {},
        pingIntervalSeconds: String(DEFAULT_PING_INTERVAL_SECONDS),
      });
      setPatchNotes([]);
      setResources([]);
      setActiveSessions([]);
      setUserModals([]);
      setLoginBackground("loginbackground.png");
      setVideoReqForm({ videoRequiredByDefault: true, exemptIncidentTypes: [], exemptSources: [] });
    }
  }, [isAdmin, fetchAdminUsers, fetchStatusTimers, fetchPatchNotes, fetchResources, fetchActiveSessions, fetchUserModals, fetchLoginBackground, fetchVideoRequirements, fetchPendingCerts, fetchHealthSummary]);

  // Auto-refresh active sessions every 30 seconds, health every 60 seconds
  useEffect(() => {
    if (!isAdmin) return;
    const sessionInterval = setInterval(() => {
      fetchActiveSessions();
    }, 30000);
    const healthInterval = setInterval(() => {
      fetchHealthSummary();
    }, 60000);
    return () => {
      clearInterval(sessionInterval);
      clearInterval(healthInterval);
    };
  }, [isAdmin, fetchActiveSessions, fetchHealthSummary]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!isAdmin) {
      return;
    }

    setSaving(true);
    setSaveError("");
    setSaveSuccess(false);

    try {
      await api.put("/users/profile", formValues);
      await refreshUser?.();
      setSaveSuccess(true);
    } catch (err) {
      console.error(err);
      const message = err.response?.data ?? "Unable to save profile changes.";
      setSaveError(typeof message === "string" ? message : "Unable to save profile changes.");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateChange = (field) => (event) => {
    const value = event.target.value;
    setCreateForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreateSubmit = async (event) => {
    event.preventDefault();

    setCreateLoading(true);
    setAdminAlert(null);

    try {
      await api.post("/auth/register", createForm);
      setAdminAlert({ type: "success", message: "Account created successfully." });
      resetCreateForm();
      fetchAdminUsers();
    } catch (err) {
      console.error(err);
      const message = err.response?.data ?? "Unable to create account.";
      setAdminAlert({
        type: "error",
        message: typeof message === "string" ? message : "Unable to create account.",
      });
    } finally {
      setCreateLoading(false);
    }
  };

  const handleAnnouncementMessageChange = (event) => {
    const { value } = event.target;
    setAnnouncementForm((prev) => ({ ...prev, message: value }));
  };

  const handleAnnouncementTypeChange = (event, nextType) => {
    if (!nextType) {
      return;
    }
    setAnnouncementForm((prev) => ({ ...prev, type: nextType }));
  };

  const handleAnnouncementPreset = (message) => {
    setAnnouncementForm((prev) => ({ ...prev, message }));
  };

  const handleAnnouncementSave = async () => {
    setAnnouncementAlert(null);

    const trimmed = announcementForm.message.trim();
    if (!trimmed) {
      setAnnouncementAlert({ severity: "error", message: "Announcement message cannot be empty." });
      return;
    }

    try {
      await updateAnnouncement({ message: trimmed, type: announcementForm.type, beaconsEnabled: announcementForm.beaconsEnabled });
      setAnnouncementAlert({ severity: "success", message: "Announcement published to all users." });
    } catch (err) {
      setAnnouncementAlert({ severity: "error", message: "Failed to publish announcement." });
    }
  };

  const handleAnnouncementClear = async () => {
    try {
      await clearAnnouncement();
      setAnnouncementAlert({ severity: "info", message: "Announcement cleared." });
    } catch (err) {
      setAnnouncementAlert({ severity: "error", message: "Failed to clear announcement." });
    }
  };

  // Patch Notes / Updates functions
  const handlePatchNoteFormChange = (field) => (event) => {
    const value = field === "showAsModal" ? event.target.checked : event.target.value;
    setPatchNoteForm((prev) => ({ ...prev, [field]: value }));
  };

  const resetPatchNoteForm = () => {
    setPatchNoteForm({
      title: "",
      content: "",
      type: "update",
      version: "",
      showAsModal: true,
      expiresAt: "",
    });
    setPatchNoteFormMode("create");
    setEditingPatchNoteId(null);
  };

  const handlePatchNoteSubmit = async (event) => {
    event.preventDefault();
    setPatchNoteSaving(true);
    setPatchNotesAlert(null);

    try {
      const payload = {
        title: patchNoteForm.title.trim(),
        content: patchNoteForm.content.trim(),
        type: patchNoteForm.type,
        version: patchNoteForm.version.trim() || null,
        showAsModal: patchNoteForm.showAsModal,
        expiresAt: patchNoteForm.expiresAt || null,
        isActive: true,
      };

      if (patchNoteFormMode === "edit" && editingPatchNoteId) {
        await api.put(`/announcements/${editingPatchNoteId}`, payload);
        setPatchNotesAlert({ severity: "success", message: "Announcement updated successfully." });
      } else {
        await api.post("/announcements", payload);
        setPatchNotesAlert({ severity: "success", message: "Announcement created successfully." });
      }

      resetPatchNoteForm();
      fetchPatchNotes();
    } catch (err) {
      console.error("Failed to save patch note:", err);
      const message = err.response?.data ?? "Failed to save announcement.";
      setPatchNotesAlert({
        severity: "error",
        message: typeof message === "string" ? message : "Failed to save announcement.",
      });
    } finally {
      setPatchNoteSaving(false);
    }
  };

  const handleEditPatchNote = (note) => {
    setPatchNoteForm({
      title: note.title || "",
      content: note.content || "",
      type: note.type || "update",
      version: note.version || "",
      showAsModal: note.showAsModal ?? true,
      expiresAt: note.expiresAt ? note.expiresAt.split("T")[0] : "",
    });
    setPatchNoteFormMode("edit");
    setEditingPatchNoteId(note.id);
  };

  const handleTogglePatchNoteActive = async (note) => {
    try {
      await api.put(`/announcements/${note.id}`, {
        ...note,
        isActive: !note.isActive,
      });
      setPatchNotesAlert({
        severity: "success",
        message: note.isActive ? "Announcement deactivated." : "Announcement activated.",
      });
      fetchPatchNotes();
    } catch (err) {
      console.error("Failed to toggle patch note status:", err);
      setPatchNotesAlert({ severity: "error", message: "Failed to update announcement status." });
    }
  };

  const handleDeletePatchNote = async (noteId) => {
    if (!window.confirm("Are you sure you want to delete this announcement?")) {
      return;
    }
    try {
      await api.delete(`/announcements/${noteId}`);
      setPatchNotesAlert({ severity: "success", message: "Announcement deleted." });
      fetchPatchNotes();
    } catch (err) {
      console.error("Failed to delete patch note:", err);
      setPatchNotesAlert({ severity: "error", message: "Failed to delete announcement." });
    }
  };

  // Resource Management handlers
  const handleResourceFormChange = (field) => (event) => {
    const value = field === "isActive" ? event.target.checked : 
                  field === "sortOrder" ? Number(event.target.value) || 0 :
                  event.target.value;
    setResourceForm((prev) => ({ ...prev, [field]: value }));
  };

  const resetResourceForm = () => {
    setResourceForm({
      type: "quick-link",
      title: "",
      description: "",
      url: "",
      category: "common",
      content: "",
      sortOrder: 0,
      isActive: true,
    });
    setResourceFormMode("create");
    setEditingResourceId(null);
  };

  const handleResourceSubmit = async (event) => {
    event.preventDefault();
    setResourceSaving(true);
    setResourcesAlert(null);

    try {
      const payload = {
        type: resourceForm.type,
        title: resourceForm.title.trim(),
        description: resourceForm.description.trim() || null,
        url: resourceForm.type === "quick-link" ? resourceForm.url.trim() : null,
        category: resourceForm.type === "quick-link" ? resourceForm.category : null,
        content: resourceForm.type !== "quick-link" ? resourceForm.content : null,
        sortOrder: resourceForm.sortOrder,
        isActive: resourceForm.isActive,
      };

      if (resourceFormMode === "edit" && editingResourceId) {
        await api.put(`/resources/${editingResourceId}`, payload);
        setResourcesAlert({ severity: "success", message: "Resource updated successfully." });
      } else {
        await api.post("/resources", payload);
        setResourcesAlert({ severity: "success", message: "Resource created successfully." });
      }

      resetResourceForm();
      fetchResources();
    } catch (err) {
      console.error("Failed to save resource:", err);
      const message = err.response?.data ?? "Failed to save resource.";
      setResourcesAlert({
        severity: "error",
        message: typeof message === "string" ? message : "Failed to save resource.",
      });
    } finally {
      setResourceSaving(false);
    }
  };

  const handleEditResource = (resource) => {
    setResourceForm({
      type: resource.type || "quick-link",
      title: resource.title || "",
      description: resource.description || "",
      url: resource.url || "",
      category: resource.category || "common",
      content: resource.content || "",
      sortOrder: resource.sortOrder || 0,
      isActive: resource.isActive ?? true,
    });
    setResourceFormMode("edit");
    setEditingResourceId(resource.id);
  };

  const handleToggleResourceActive = async (resource) => {
    try {
      await api.put(`/resources/${resource.id}`, {
        isActive: !resource.isActive,
      });
      setResourcesAlert({
        severity: "success",
        message: resource.isActive ? "Resource hidden." : "Resource visible.",
      });
      fetchResources();
    } catch (err) {
      console.error("Failed to toggle resource status:", err);
      setResourcesAlert({ severity: "error", message: "Failed to update resource status." });
    }
  };

  const handleDeleteResource = async (resourceId) => {
    if (!window.confirm("Are you sure you want to delete this resource?")) {
      return;
    }
    try {
      await api.delete(`/resources/${resourceId}`);
      setResourcesAlert({ severity: "success", message: "Resource deleted." });
      fetchResources();
    } catch (err) {
      console.error("Failed to delete resource:", err);
      setResourcesAlert({ severity: "error", message: "Failed to delete resource." });
    }
  };

  const filteredResources = useMemo(() => {
    if (resourceTypeFilter === "all") {
      return resources;
    }
    return resources.filter((r) => r.type === resourceTypeFilter);
  }, [resources, resourceTypeFilter]);

  // ICS-205 Editor handlers
  const handleIcs205FieldChange = (field, value) => {
    setIcs205Form((prev) => ({ ...prev, [field]: value }));
  };

  const handleIcs205OperationalPeriodChange = (field, value) => {
    setIcs205Form((prev) => ({
      ...prev,
      operationalPeriod: { ...prev.operationalPeriod, [field]: value },
    }));
  };

  const handleIcs205PreparedByChange = (field, value) => {
    setIcs205Form((prev) => ({
      ...prev,
      preparedBy: { ...prev.preparedBy, [field]: value },
    }));
  };

  const handleIcs205ChannelChange = (index, field, value) => {
    setIcs205Form((prev) => {
      const newChannels = [...prev.basicRadioChannelUse];
      newChannels[index] = { ...newChannels[index], [field]: value };
      return { ...prev, basicRadioChannelUse: newChannels };
    });
  };

  const handleAddIcs205Channel = () => {
    setIcs205Form((prev) => ({
      ...prev,
      basicRadioChannelUse: [
        ...prev.basicRadioChannelUse,
        { zone: "", channel: "", function: "", assignment: "", rxFreq: "", rxTone: "", txFreq: "", txTone: "", mode: "A", remarks: "" },
      ],
    }));
  };

  const handleRemoveIcs205Channel = (index) => {
    setIcs205Form((prev) => ({
      ...prev,
      basicRadioChannelUse: prev.basicRadioChannelUse.filter((_, i) => i !== index),
    }));
  };

  const handleIcs205InstructionChange = (index, value) => {
    setIcs205Form((prev) => {
      const newInstructions = [...prev.specialInstructions];
      newInstructions[index] = value;
      return { ...prev, specialInstructions: newInstructions };
    });
  };

  const handleAddIcs205Instruction = () => {
    setIcs205Form((prev) => ({
      ...prev,
      specialInstructions: [...prev.specialInstructions, ""],
    }));
  };

  const handleRemoveIcs205Instruction = (index) => {
    setIcs205Form((prev) => ({
      ...prev,
      specialInstructions: prev.specialInstructions.filter((_, i) => i !== index),
    }));
  };

  const handleSaveIcs205 = async () => {
    setResourceSaving(true);
    setResourcesAlert(null);
    
    try {
      const payload = {
        type: "ics-205",
        title: "ICS-205 Radio Communications Plan",
        description: "Incident Radio Communications Plan",
        content: JSON.stringify(ics205Form),
        sortOrder: 0,
        isActive: true,
      };

      if (ics205ResourceId) {
        await api.put(`/resources/${ics205ResourceId}`, payload);
        setResourcesAlert({ severity: "success", message: "ICS-205 updated successfully." });
      } else {
        const res = await api.post("/resources", payload);
        setIcs205ResourceId(res.data.id);
        setResourcesAlert({ severity: "success", message: "ICS-205 created successfully." });
      }
      fetchResources();
    } catch (err) {
      console.error("Failed to save ICS-205:", err);
      setResourcesAlert({ severity: "error", message: "Failed to save ICS-205." });
    } finally {
      setResourceSaving(false);
    }
  };

  // Maintenance mode handlers
  const handleMaintenanceToggle = async () => {
    const newEnabled = !maintenanceForm.enabled;
    try {
      setMaintenanceForm(prev => ({ ...prev, enabled: newEnabled }));
      await setMaintenanceMode(newEnabled, maintenanceForm.message);
      setMaintenanceAlert({
        severity: newEnabled ? "warning" : "success",
        message: newEnabled
          ? "Maintenance mode ENABLED. Non-admin users can only access Dashboard."
          : "Maintenance mode DISABLED. All pages are accessible.",
      });
    } catch (err) {
      console.error('Failed to toggle maintenance mode:', err);
      // Revert the form state on error
      setMaintenanceForm(prev => ({ ...prev, enabled: !newEnabled }));
      setMaintenanceAlert({
        severity: "error",
        message: "Failed to update maintenance mode. Please try again.",
      });
    }
  };

  const handleMaintenanceMessageChange = (event) => {
    setMaintenanceForm(prev => ({ ...prev, message: event.target.value }));
  };

  const handleMaintenanceMessageSave = async () => {
    try {
      await setMaintenanceMode(maintenanceForm.enabled, maintenanceForm.message);
      setMaintenanceAlert({ 
        severity: "success", 
        message: maintenanceForm.message.trim() ? "Maintenance message updated." : "Maintenance message cleared." 
      });
    } catch (err) {
      console.error('Failed to save maintenance message:', err);
      setMaintenanceAlert({
        severity: "error",
        message: "Failed to update maintenance message. Please try again.",
      });
    }
  };

  const handleStatusToggle = async (targetId, nextDisabled) => {
    setAdminAlert(null);

    try {
      await api.put(`/users/${targetId}/status`, { isDisabled: nextDisabled });
      setAdminAlert({
        type: "success",
        message: nextDisabled ? "Account disabled." : "Account enabled.",
      });
      fetchAdminUsers();
    } catch (err) {
      console.error(err);
      const message = err.response?.data ?? "Unable to update account status.";
      setAdminAlert({
        type: "error",
        message: typeof message === "string" ? message : "Unable to update account status.",
      });
    }
  };

  const handleImpersonate = async (targetId) => {
    setAdminAlert(null);

    try {
      const res = await api.post("/auth/impersonate", { userId: targetId });
      login(res.data.token, res.data.user);
    } catch (err) {
      console.error(err);
      const message = err.response?.data ?? "Unable to impersonate this account.";
      setAdminAlert({
        type: "error",
        message: typeof message === "string" ? message : "Unable to impersonate this account.",
      });
    }
  };

  const openDeleteDialog = (account) => {
    setDeleteTarget(account);
    setDeleteError("");
  };

  const closeDeleteDialog = () => {
    if (!deleteLoading) {
      setDeleteTarget(null);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deleteTarget) {
      return;
    }

    setDeleteLoading(true);
    setDeleteError("");
    setAdminAlert(null);

    try {
      await api.delete(`/users/${deleteTarget.id}`);
      setAdminAlert({ type: "success", message: "Account deleted." });
      setDeleteTarget(null);
      fetchAdminUsers();
    } catch (err) {
      console.error(err);
      const message = err.response?.data ?? "Unable to delete this account.";
      setDeleteError(typeof message === "string" ? message : "Unable to delete this account.");
    } finally {
      setDeleteLoading(false);
    }
  };

  const isProtectedAccount = useCallback(
    (account) => {
      if (!account) {
        return false;
      }

      const username = (account.username ?? "").toLowerCase();
      return username === "admin" || account.id === user?.id;
    },
    [user?.id]
  );

  if (!isAdmin) {
    return (
      <DashboardLayout title="Admin Panel">
        <Alert severity="error">You need administrator permissions to view this page.</Alert>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Admin Panel">
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper elevation={1} sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>
              Edit Profile
            </Typography>

            <Divider sx={{ mb: 2 }} />

            {saveSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Profile saved successfully.
              </Alert>
            )}

            {saveError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {saveError}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField label="Name" value={formValues.name} onChange={handleChange("name")} fullWidth />
              <TextField label="Email" type="email" value={formValues.email} onChange={handleChange("email")} fullWidth />
              <TextField label="Phone Number" value={formValues.phoneNumber} onChange={handleChange("phoneNumber")} fullWidth />
              <TextField label="Ranger ID" value={formValues.rangerId} onChange={handleChange("rangerId")} fullWidth />
              <TextField label="Division" value={formValues.division} onChange={handleChange("division")} fullWidth />

              <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ mt: 1 }}>
                <Button type="submit" variant="contained" disabled={saving}>
                  {saving ? "Saving..." : "Save Changes"}
                </Button>
              </Stack>
            </Box>
          </Paper>
        </Grid>

        {/* Active Sessions - Online Users */}
        <Grid item xs={12} md={6}>
          <Paper elevation={1} sx={{ p: 3 }}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
              <Box>
                <Typography variant="h5">Active Sessions</Typography>
                <Typography variant="body2" color="text.secondary">
                  Currently online users (not stale)
                </Typography>
              </Box>
              <IconButton onClick={fetchActiveSessions} disabled={activeSessionsLoading} size="small">
                {activeSessionsLoading ? <CircularProgress size={20} /> : "🔄"}
              </IconButton>
            </Stack>
            <Divider sx={{ mb: 2 }} />
            {activeSessions.length === 0 ? (
              <Typography color="text.secondary" sx={{ textAlign: "center", py: 2 }}>
                No active users online
              </Typography>
            ) : (
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Ranger</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Client</TableCell>
                      <TableCell>IP</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activeSessions.map((session) => (
                      <TableRow key={session.userId} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {session.rangerId || session.username || `User ${session.userId}`}
                          </Typography>
                          {session.name && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {session.name}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={session.status?.toUpperCase() || "UNKNOWN"}
                            size="small"
                            color={
                              session.status === "emergency" ? "error" :
                              session.status === "busy" ? "warning" :
                              session.status === "available" ? "success" : "default"
                            }
                            sx={{ fontSize: "0.7rem" }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                            {session.isMobile ? "📱 Mobile" : "💻 Desktop"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: "0.7rem" }}>
                            {session.browserInfo}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>
                            {session.ipAddress || "—"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1, textAlign: "right" }}>
              {activeSessions.length} active user{activeSessions.length !== 1 ? "s" : ""} • Auto-refreshes every 30s
            </Typography>
          </Paper>
        </Grid>

        {/* System Health Dashboard */}
        <Grid item xs={12} md={6}>
          <Paper elevation={1} sx={{ p: 3 }}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
              <Box>
                <Typography variant="h5">System Health</Typography>
                <Typography variant="body2" color="text.secondary">
                  Server uptime, database, and storage
                </Typography>
              </Box>
              <IconButton onClick={fetchHealthSummary} disabled={healthLoading} size="small">
                {healthLoading ? <CircularProgress size={20} /> : "🔄"}
              </IconButton>
            </Stack>
            <Divider sx={{ mb: 2 }} />
            {healthError && (
              <Alert severity="error" sx={{ mb: 2 }}>{healthError}</Alert>
            )}
            {!healthSummary && !healthLoading && !healthError && (
              <Typography color="text.secondary" sx={{ textAlign: "center", py: 2 }}>
                No health data available
              </Typography>
            )}
            {healthSummary && (
              <Stack spacing={2}>
                {/* Uptime */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography variant="body2" fontWeight={600}>⏱ Uptime</Typography>
                  <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                    {healthSummary.uptimeFormatted || "—"}
                  </Typography>
                </Box>
                <Divider />
                {/* Database Size */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography variant="body2" fontWeight={600}>🗄 Database</Typography>
                  <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                    {healthSummary.databaseSizeMB != null ? `${healthSummary.databaseSizeMB.toFixed(1)} MB` : "—"}
                  </Typography>
                </Box>
                <Divider />
                {/* Storage */}
                {healthSummary.storageVolumes && healthSummary.storageVolumes.length > 0 && (
                  <>
                    {healthSummary.storageVolumes.map((vol, idx) => {
                      const usedPct = vol.totalGB > 0 ? ((vol.usedGB / vol.totalGB) * 100) : 0;
                      const color = usedPct > 90 ? "error" : usedPct > 75 ? "warning" : "success";
                      return (
                        <Box key={idx}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                            <Typography variant="body2" fontWeight={600}>💾 {vol.mount || vol.filesystem}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {vol.usedGB?.toFixed(1)}G / {vol.totalGB?.toFixed(1)}G ({usedPct.toFixed(0)}%)
                            </Typography>
                          </Box>
                          <Box sx={{ width: "100%", bgcolor: "grey.800", borderRadius: 1, height: 8 }}>
                            <Box sx={{ width: `${Math.min(usedPct, 100)}%`, bgcolor: `${color}.main`, borderRadius: 1, height: 8, transition: "width 0.5s ease" }} />
                          </Box>
                        </Box>
                      );
                    })}
                  </>
                )}
              </Stack>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5, textAlign: "right" }}>
              Auto-refreshes every 60s
            </Typography>
          </Paper>
        </Grid>

        {/* User Modals - Targeted Alerts */}
        <Grid item xs={12} md={6}>
          <Paper elevation={1} sx={{ p: 3 }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h5">User Modals</Typography>
              <Typography variant="body2" color="text.secondary">
                Create persistent alerts for specific users that display on every page
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            
            {userModalsAlert && (
              <Alert severity={userModalsAlert.severity} sx={{ mb: 2 }} onClose={() => setUserModalsAlert(null)}>
                {userModalsAlert.message}
              </Alert>
            )}

            {/* Create New Modal Form */}
            <Box component="form" onSubmit={handleCreateUserModal} sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Create New Modal</Typography>
              <Stack spacing={2}>
                <TextField
                  select
                  label="Target User"
                  value={userModalForm.targetUserId}
                  onChange={handleUserModalFormChange("targetUserId")}
                  size="small"
                  fullWidth
                  required
                >
                  {adminUsers
                    .filter(u => !u.isDisabled)
                    .map((u) => (
                      <MenuItem key={u.id} value={u.id}>
                        {u.rangerId || u.username} - {u.name}
                      </MenuItem>
                    ))}
                </TextField>
                <TextField
                  label="Title"
                  value={userModalForm.title}
                  onChange={handleUserModalFormChange("title")}
                  size="small"
                  fullWidth
                  required
                  inputProps={{ maxLength: 200 }}
                />
                <TextField
                  label="Message"
                  value={userModalForm.message}
                  onChange={handleUserModalFormChange("message")}
                  size="small"
                  fullWidth
                  required
                  multiline
                  rows={3}
                  inputProps={{ maxLength: 2000 }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  disabled={userModalSaving}
                  size="small"
                >
                  {userModalSaving ? "Creating..." : "Create Modal"}
                </Button>
              </Stack>
            </Box>

            <Divider sx={{ mb: 2 }} />
            
            {/* Active Modals List */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Active Modals</Typography>
            {userModalsLoading ? (
              <Box sx={{ textAlign: "center", py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : userModals.filter(m => m.isActive).length === 0 ? (
              <Typography color="text.secondary" sx={{ textAlign: "center", py: 2 }}>
                No active user modals
              </Typography>
            ) : (
              <Stack spacing={1}>
                {userModals.filter(m => m.isActive).map((modal) => (
                  <Paper key={modal.id} variant="outlined" sx={{ p: 1.5 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {modal.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Target: {modal.targetRangerId || `User ${modal.targetUserId}`} ({modal.targetUserName})
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5, fontSize: "0.85rem" }}>
                          {modal.message.length > 100 ? `${modal.message.substring(0, 100)}...` : modal.message}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          Created {formatTimestamp(modal.createdAtUtc)} by {modal.createdByUserName}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={0.5}>
                        <Button
                          size="small"
                          color="warning"
                          onClick={() => handleDeactivateUserModal(modal.id)}
                        >
                          Deactivate
                        </Button>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteUserModal(modal.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            )}

            {/* Inactive Modals */}
            {userModals.filter(m => !m.isActive).length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ mb: 1 }} color="text.secondary">
                  Deactivated Modals ({userModals.filter(m => !m.isActive).length})
                </Typography>
                <Stack spacing={1}>
                  {userModals.filter(m => !m.isActive).slice(0, 5).map((modal) => (
                    <Paper key={modal.id} variant="outlined" sx={{ p: 1, opacity: 0.7 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {modal.title} → {modal.targetRangerId || modal.targetUserName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Deactivated {formatTimestamp(modal.deactivatedAtUtc)} by {modal.deactivatedByUserName}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteUserModal(modal.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              </>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper elevation={1} sx={{ p: 3 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              sx={{ mb: 2 }}
            >
              <Box>
                <Typography variant="h5">Account Management</Typography>
                <Typography variant="body2" color="text.secondary">
                  Create, disable, delete, or impersonate RangerVault accounts.
                </Typography>
              </Box>

              <Button variant="outlined" onClick={fetchAdminUsers} disabled={adminUsersLoading}>
                {adminUsersLoading ? "Refreshing..." : "Refresh"}
              </Button>
            </Stack>

            {adminAlert && (
              <Alert severity={adminAlert.type} sx={{ mb: 2 }}>
                {adminAlert.message}
              </Alert>
            )}

            <Grid container spacing={3}>
              <Grid item xs={12} md={5}>
                <Typography variant="subtitle1" gutterBottom>
                  Create Account
                </Typography>
                <Box component="form" onSubmit={handleCreateSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <TextField label="Username" value={createForm.username} onChange={handleCreateChange("username")} required />
                  <TextField label="Password" type="password" value={createForm.password} onChange={handleCreateChange("password")} required />
                  <TextField select label="Role" value={createForm.role} onChange={handleCreateChange("role")} required>
                    {roleOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option.charAt(0).toUpperCase() + option.slice(1)}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField label="Name" value={createForm.name} onChange={handleCreateChange("name")} />
                  <TextField label="Email" type="email" value={createForm.email} onChange={handleCreateChange("email")} />
                  <TextField label="Phone Number" value={createForm.phoneNumber} onChange={handleCreateChange("phoneNumber")} />
                  <TextField label="Ranger ID" value={createForm.rangerId} onChange={handleCreateChange("rangerId")} />
                  <TextField label="Division" value={createForm.division} onChange={handleCreateChange("division")} />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={createForm.canViewComms}
                        onChange={(e) => setCreateForm((prev) => ({ ...prev, canViewComms: e.target.checked }))}
                      />
                    }
                    label="View Comms (ICS-205 / Radio)"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={createForm.incidentCreationRestricted}
                        onChange={(e) => setCreateForm((prev) => ({ ...prev, incidentCreationRestricted: e.target.checked }))}
                      />
                    }
                    label="Restrict Incident Creation (Core Classes Required)"
                  />

                  <Stack direction="row" justifyContent="flex-end">
                    <Button type="submit" variant="contained" disabled={createLoading}>
                      {createLoading ? "Creating..." : "Create Account"}
                    </Button>
                  </Stack>
                </Box>
              </Grid>

              <Grid item xs={12} md={7}>
                <Typography variant="subtitle1" gutterBottom>
                  Existing Accounts
                </Typography>

                {adminUsersLoading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress size={28} />
                  </Box>
                ) : adminUsersError ? (
                  <Alert severity="error">{adminUsersError}</Alert>
                ) : adminUsers.length === 0 ? (
                  <Alert severity="info">No accounts found.</Alert>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Username</TableCell>
                          <TableCell>Role</TableCell>
                          <TableCell>Ranger ID</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {adminUsers.map((account) => (
                          <TableRow key={account.id} hover>
                            <TableCell>{formatValue(account.name)}</TableCell>
                            <TableCell>{account.username}</TableCell>
                            <TableCell>{account.role}</TableCell>
                            <TableCell>{account.rangerId ?? "—"}</TableCell>
                            <TableCell>
                              <Chip size="small" label={account.isDisabled ? "Disabled" : "Active"} color={account.isDisabled ? "default" : "success"} />
                            </TableCell>
                            <TableCell align="right">
                              <Stack direction="row" spacing={1} justifyContent="flex-end">
                                <Button size="small" variant="outlined" onClick={() => openEditDialog(account)}>
                                  Edit
                                </Button>
                                <Button size="small" variant="outlined" onClick={() => handleStatusToggle(account.id, !account.isDisabled)} disabled={user?.id === account.id}>
                                  {account.isDisabled ? "Enable" : "Disable"}
                                </Button>
                                <Button size="small" variant="contained" color="secondary" onClick={() => handleImpersonate(account.id)} disabled={account.isDisabled}>
                                  Impersonate
                                </Button>
                                <Button size="small" variant="outlined" color="error" onClick={() => openDeleteDialog(account)} disabled={isProtectedAccount(account)}>
                                  Delete
                                </Button>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper elevation={1} sx={{ p: 3 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              sx={{ mb: 2 }}
            >
              <Box>
                <Typography variant="h5">
                  Certification Review
                  {pendingCerts.length > 0 && (
                    <Chip label={pendingCerts.length} color="warning" size="small" sx={{ ml: 1, fontWeight: 700 }} />
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Review and approve or reject training certification uploads from Rangers.
                </Typography>
              </Box>
              <Button variant="outlined" onClick={fetchPendingCerts} disabled={pendingCertsLoading}>
                {pendingCertsLoading ? "Refreshing..." : "Refresh"}
              </Button>
            </Stack>

            {pendingCertsAlert && (
              <Alert severity={pendingCertsAlert.type} sx={{ mb: 2 }} onClose={() => setPendingCertsAlert(null)}>
                {pendingCertsAlert.message}
              </Alert>
            )}

            {pendingCertsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            ) : pendingCerts.length === 0 ? (
              <Alert severity="info">No certifications pending review.</Alert>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Ranger</TableCell>
                      <TableCell>Course</TableCell>
                      <TableCell>Provider</TableCell>
                      <TableCell>Completion Date</TableCell>
                      <TableCell>Uploaded</TableCell>
                      <TableCell>PDF</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pendingCerts.map((cert) => (
                      <TableRow key={cert.id} hover>
                        <TableCell>
                          <Stack>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{cert.rangerName || cert.username}</Typography>
                            {cert.rangerId && <Typography variant="caption" color="text.secondary">{cert.rangerId}</Typography>}
                          </Stack>
                        </TableCell>
                        <TableCell>{cert.courseName}</TableCell>
                        <TableCell><Chip label={cert.provider} size="small" /></TableCell>
                        <TableCell>{new Date(cert.completionDate).toLocaleDateString()}</TableCell>
                        <TableCell>{new Date(cert.createdAtUtc).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            onClick={async () => {
                              try {
                                const res = await api.get(`/training-certifications/${cert.id}/download`);
                                window.open(res.data.url, "_blank");
                              } catch {
                                setPendingCertsAlert({ type: "error", message: "Failed to open PDF" });
                              }
                            }}
                          >
                            View
                          </Button>
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              disabled={reviewLoading === cert.id}
                              onClick={() => handleApproveCert(cert.id)}
                            >
                              {reviewLoading === cert.id ? <CircularProgress size={18} /> : "Approve"}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              disabled={reviewLoading === cert.id}
                              onClick={() => setRejectDialog({ open: true, certId: cert.id, notes: "" })}
                            >
                              Reject
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper elevation={1} sx={{ p: 3 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              sx={{ mb: 2 }}
            >
              <Box>
                <Typography variant="h5">System Announcement</Typography>
                <Typography variant="body2" color="text.secondary">
                  Publish banner messages to alert all Rangers of maintenance, outages, or important updates.
                </Typography>
              </Box>

              <Stack direction="row" spacing={1} flexWrap="wrap">
                {presetAnnouncements.map((preset) => (
                  <Button key={preset.label} variant="outlined" size="small" onClick={() => handleAnnouncementPreset(preset.message)}>
                    {preset.label}
                  </Button>
                ))}
              </Stack>
            </Stack>

            {announcementAlert && (
              <Alert severity={announcementAlert.severity} sx={{ mb: 2 }}>
                {announcementAlert.message}
              </Alert>
            )}

            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Message Type
                </Typography>
                <ToggleButtonGroup
                  value={announcementForm.type}
                  exclusive
                  onChange={handleAnnouncementTypeChange}
                  color={announcementForm.type === "urgent" ? "error" : announcementForm.type === "warning" ? "warning" : "success"}
                  size="small"
                >
                  <ToggleButton value="general">General</ToggleButton>
                  <ToggleButton value="warning">Warning</ToggleButton>
                  <ToggleButton value="urgent">Urgent</ToggleButton>
                </ToggleButtonGroup>
              </Box>

              <TextField
                label="Announcement message"
                multiline
                minRows={3}
                value={announcementForm.message}
                onChange={handleAnnouncementMessageChange}
                placeholder="Describe the maintenance window, outage, or advisory."
                fullWidth
              />

              <Box>
                <Button
                  variant={announcementForm.beaconsEnabled ? "contained" : "outlined"}
                  color="warning"
                  onClick={() => setAnnouncementForm((prev) => ({ ...prev, beaconsEnabled: !prev.beaconsEnabled }))}
                  startIcon={<span style={{ fontSize: 18 }}>🚨</span>}
                  sx={{
                    fontWeight: 600,
                    ...(announcementForm.beaconsEnabled && {
                      animation: "beacon-pulse 1s ease-in-out infinite",
                      "@keyframes beacon-pulse": {
                        "0%, 100%": { boxShadow: "0 0 5px 2px rgba(255, 152, 0, 0.4)" },
                        "50%": { boxShadow: "0 0 15px 5px rgba(255, 152, 0, 0.8)" },
                      },
                    }),
                  }}
                >
                  {announcementForm.beaconsEnabled ? "Beacons Enabled" : "Enable Beacons"}
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                  Simulates flashing amber beacons like a highway message sign
                </Typography>
              </Box>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                justifyContent="flex-end"
                alignItems={{ xs: "stretch", sm: "center" }}
              >
                <Button variant="text" color="inherit" onClick={handleAnnouncementClear} disabled={!announcement?.message}>
                  Clear
                </Button>
                <Button variant="contained" onClick={handleAnnouncementSave}>
                  Publish Announcement
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Grid>

        {/* Maintenance Mode Section */}
        <Grid item xs={12} md={6}>
          <Paper 
            elevation={1} 
            sx={{ 
              p: 3,
              border: maintenanceForm.enabled ? '3px solid' : 'none',
              borderColor: 'warning.main',
            }}
          >
            <Stack spacing={2}>
              <Box>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Typography variant="h5">Maintenance Mode</Typography>
                  <Chip 
                    label={maintenanceForm.enabled ? "ACTIVE" : "INACTIVE"} 
                    color={maintenanceForm.enabled ? "warning" : "default"}
                    size="small"
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Enable to restrict access to login and dashboard only. Admins can still access all pages.
                </Typography>
              </Box>

              {maintenanceAlert && (
                <Alert severity={maintenanceAlert.severity} sx={{ mb: 1 }}>
                  {maintenanceAlert.message}
                </Alert>
              )}

              <Button
                variant={maintenanceForm.enabled ? "contained" : "outlined"}
                color={maintenanceForm.enabled ? "warning" : "primary"}
                onClick={handleMaintenanceToggle}
                fullWidth
                sx={{ py: 1.5, fontWeight: 700 }}
              >
                {maintenanceForm.enabled ? "🚧 DISABLE MAINTENANCE MODE" : "🔧 ENABLE MAINTENANCE MODE"}
              </Button>

              <TextField
                label="Maintenance Message (optional)"
                multiline
                minRows={2}
                value={maintenanceForm.message}
                onChange={handleMaintenanceMessageChange}
                placeholder="Describe what maintenance is being performed..."
                fullWidth
              />

              <Button 
                variant="text" 
                onClick={handleMaintenanceMessageSave}
              >
                Update Message
              </Button>
            </Stack>
          </Paper>
        </Grid>

        {/* Login Background Selector Section */}
        <Grid item xs={12} md={6}>
          <Paper elevation={1} sx={{ p: 3 }}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="h5">Login Background</Typography>
                <Typography variant="body2" color="text.secondary">
                  Select the background image shown on the login page.
                </Typography>
              </Box>

              {loginBackgroundAlert && (
                <Alert 
                  severity={loginBackgroundAlert.severity} 
                  onClose={() => setLoginBackgroundAlert(null)}
                  sx={{ mb: 1 }}
                >
                  {loginBackgroundAlert.message}
                </Alert>
              )}

              {loginBackgroundLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                  <Typography>Loading...</Typography>
                </Box>
              ) : (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                  {loginBackgroundOptions.map((option) => (
                    <Box
                      key={option.value}
                      onClick={() => !loginBackgroundSaving && handleLoginBackgroundChange(option.value)}
                      sx={{
                        width: "calc(33.33% - 12px)",
                        minWidth: 80,
                        cursor: loginBackgroundSaving ? "not-allowed" : "pointer",
                        border: loginBackground === option.value ? "3px solid" : "1px solid",
                        borderColor: loginBackground === option.value ? "primary.main" : "grey.300",
                        borderRadius: 1,
                        overflow: "hidden",
                        opacity: loginBackgroundSaving ? 0.6 : 1,
                        transition: "all 0.2s ease",
                        "&:hover": {
                          borderColor: loginBackgroundSaving ? undefined : "primary.light",
                          transform: loginBackgroundSaving ? undefined : "scale(1.02)",
                        },
                      }}
                    >
                      <Box
                        component="img"
                        src={`/${option.value}`}
                        alt={option.label}
                        sx={{
                          width: "100%",
                          height: 80,
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                      <Box sx={{ p: 0.5, textAlign: "center" }}>
                        <Typography 
                          variant="caption" 
                          color={loginBackground === option.value ? "primary" : "text.secondary"}
                          fontWeight={loginBackground === option.value ? 700 : 400}
                        >
                          {option.label}
                          {loginBackground === option.value && " ✓"}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}

              {loginBackgroundSaving && (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>
                  Saving...
                </Typography>
              )}
            </Stack>
          </Paper>
        </Grid>

        {/* Support Tickets section removed */}

        <Grid item xs={12}>
          <Paper elevation={1} sx={{ p: 3 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              sx={{ mb: 2 }}
            >
              <Box>
                <Typography variant="h5">Status Check Timers</Typography>
                <Typography variant="body2" color="text.secondary">
                  Set the default check-in window and override it per incident type.
                </Typography>
              </Box>

              <Button variant="outlined" onClick={fetchStatusTimers} disabled={statusTimerLoading}>
                {statusTimerLoading ? "Loading..." : "Reload"}
              </Button>
            </Stack>

            {statusTimerAlert && (
              <Alert severity={statusTimerAlert.severity} sx={{ mb: 2 }}>
                {statusTimerAlert.message}
              </Alert>
            )}

            <Box component="form" onSubmit={handleStatusTimerSave} sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <TextField
                label="Default minutes before status check"
                type="number"
                value={statusTimerForm.defaultMinutes}
                onChange={handleStatusTimerDefaultChange}
                inputProps={{ min: 1 }}
                required
              />

              <TextField
                label={`Ping interval (seconds)`}
                type="number"
                value={statusTimerForm.pingIntervalSeconds}
                onChange={handlePingIntervalChange}
                inputProps={{ min: MIN_PING_INTERVAL_SECONDS, max: MAX_PING_INTERVAL_SECONDS }}
                helperText={`Controls how often clients ping the server (${MIN_PING_INTERVAL_SECONDS}-${MAX_PING_INTERVAL_SECONDS}s).`}
                required
              />

              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Per incident type overrides (leave blank to use default)
                </Typography>

                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Incident Type</TableCell>
                        <TableCell align="right">Minutes</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {INCIDENT_TYPES.map((type) => (
                        <TableRow key={type} hover>
                          <TableCell>{type}</TableCell>
                          <TableCell align="right">
                            <TextField
                              type="number"
                              size="small"
                              value={statusTimerForm.overrides[type] ?? ""}
                              onChange={handleStatusTimerOverrideChange(type)}
                              inputProps={{ min: 1 }}
                              placeholder={statusTimerForm.defaultMinutes || "Default"}
                              disabled={statusTimerLoading || statusTimerSaving}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <Stack direction="row" justifyContent="flex-end" spacing={2}>
                <Button type="button" variant="text" onClick={fetchStatusTimers} disabled={statusTimerLoading || statusTimerSaving}>
                  Reset
                </Button>
                <Button type="submit" variant="contained" disabled={statusTimerSaving}>
                  {statusTimerSaving ? "Saving..." : "Save Timers"}
                </Button>
              </Stack>
            </Box>
          </Paper>
        </Grid>

        {/* Video Requirements Section */}
        <Grid item xs={12}>
          <Paper elevation={1} sx={{ p: 3 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              sx={{ mb: 2 }}
            >
              <Box>
                <Typography variant="h5">Video Requirements</Typography>
                <Typography variant="body2" color="text.secondary">
                  Configure which incident types and sources require video evidence (2026+ incidents).
                </Typography>
              </Box>
              <Button variant="outlined" onClick={fetchVideoRequirements} disabled={videoReqLoading}>
                {videoReqLoading ? "Loading..." : "Reload"}
              </Button>
            </Stack>

            {videoReqAlert && (
              <Alert severity={videoReqAlert.severity} sx={{ mb: 2 }}>
                {videoReqAlert.message}
              </Alert>
            )}

            <Box component="form" onSubmit={handleVideoReqSave} sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <input
                  type="checkbox"
                  id="videoRequiredByDefault"
                  checked={videoReqForm.videoRequiredByDefault}
                  onChange={(e) => setVideoReqForm((prev) => ({ ...prev, videoRequiredByDefault: e.target.checked }))}
                />
                <label htmlFor="videoRequiredByDefault">
                  <Typography variant="body1">
                    Video required by default for all 2026+ incidents
                  </Typography>
                </label>
              </Stack>

              {videoReqForm.videoRequiredByDefault && (
                <>
                  <Box>
                    <Typography variant="subtitle1" gutterBottom>
                      Exempt Incident Types (will NOT require video)
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={1}>
                      {INCIDENT_TYPES.map((type) => {
                        const isExempt = (videoReqForm.exemptIncidentTypes || []).includes(type.toUpperCase());
                        return (
                          <Chip
                            key={type}
                            label={type}
                            color={isExempt ? "warning" : "default"}
                            variant={isExempt ? "filled" : "outlined"}
                            onClick={() => handleVideoReqToggleType(type)}
                            sx={{ cursor: "pointer" }}
                          />
                        );
                      })}
                    </Stack>
                  </Box>

                  <Box>
                    <Typography variant="subtitle1" gutterBottom>
                      Exempt Sources (will NOT require video)
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={1}>
                      {SOURCE_OPTIONS.map((source) => {
                        const isExempt = (videoReqForm.exemptSources || []).includes(source.toUpperCase());
                        return (
                          <Chip
                            key={source}
                            label={source}
                            color={isExempt ? "warning" : "default"}
                            variant={isExempt ? "filled" : "outlined"}
                            onClick={() => handleVideoReqToggleSource(source)}
                            sx={{ cursor: "pointer" }}
                          />
                        );
                      })}
                    </Stack>
                  </Box>
                </>
              )}

              <Stack direction="row" justifyContent="flex-end" spacing={2}>
                <Button type="button" variant="text" onClick={fetchVideoRequirements} disabled={videoReqLoading || videoReqSaving}>
                  Reset
                </Button>
                <Button type="submit" variant="contained" disabled={videoReqSaving}>
                  {videoReqSaving ? "Saving..." : "Save Video Requirements"}
                </Button>
              </Stack>
            </Box>
          </Paper>
        </Grid>

        {/* Patch Notes / Updates Section */}
        <Grid item xs={12}>
          <Paper elevation={1} sx={{ p: 3 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              sx={{ mb: 2 }}
            >
              <Box>
                <Typography variant="h5">📢 Updates & Patch Notes</Typography>
                <Typography variant="body2" color="text.secondary">
                  Create announcements that display as a popup modal for desktop users.
                </Typography>
              </Box>

              <Button variant="outlined" onClick={fetchPatchNotes} disabled={patchNotesLoading}>
                {patchNotesLoading ? "Loading..." : "Refresh"}
              </Button>
            </Stack>

            {patchNotesAlert && (
              <Alert severity={patchNotesAlert.severity} sx={{ mb: 2 }} onClose={() => setPatchNotesAlert(null)}>
                {patchNotesAlert.message}
              </Alert>
            )}

            <Grid container spacing={3}>
              <Grid item xs={12} md={5}>
                <Typography variant="subtitle1" gutterBottom>
                  {patchNoteFormMode === "edit" ? "Edit Announcement" : "Create Announcement"}
                </Typography>
                <Box component="form" onSubmit={handlePatchNoteSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <TextField
                    label="Title"
                    value={patchNoteForm.title}
                    onChange={handlePatchNoteFormChange("title")}
                    required
                    placeholder="e.g., Version 2.0 Release"
                  />
                  <TextField
                    label="Content"
                    multiline
                    minRows={4}
                    value={patchNoteForm.content}
                    onChange={handlePatchNoteFormChange("content")}
                    required
                    placeholder="Describe the update, new features, bug fixes, etc."
                  />
                  <TextField
                    select
                    label="Type"
                    value={patchNoteForm.type}
                    onChange={handlePatchNoteFormChange("type")}
                    required
                  >
                    <MenuItem value="update">🚀 Update</MenuItem>
                    <MenuItem value="patch">🔧 Patch</MenuItem>
                    <MenuItem value="alert">⚠️ Alert</MenuItem>
                    <MenuItem value="info">ℹ️ Info</MenuItem>
                  </TextField>
                  <TextField
                    label="Version (optional)"
                    value={patchNoteForm.version}
                    onChange={handlePatchNoteFormChange("version")}
                    placeholder="e.g., v2.0.1"
                  />
                  <TextField
                    label="Expires On (optional)"
                    type="date"
                    value={patchNoteForm.expiresAt}
                    onChange={handlePatchNoteFormChange("expiresAt")}
                    InputLabelProps={{ shrink: true }}
                    helperText="Leave empty for no expiration"
                  />
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <input
                      type="checkbox"
                      id="showAsModal"
                      checked={patchNoteForm.showAsModal}
                      onChange={handlePatchNoteFormChange("showAsModal")}
                    />
                    <label htmlFor="showAsModal">
                      <Typography variant="body2">Show as popup modal for desktop users</Typography>
                    </label>
                  </Stack>
                  <Stack direction="row" justifyContent="flex-end" spacing={1}>
                    {patchNoteFormMode === "edit" && (
                      <Button type="button" variant="text" onClick={resetPatchNoteForm}>
                        Cancel
                      </Button>
                    )}
                    <Button type="submit" variant="contained" disabled={patchNoteSaving}>
                      {patchNoteSaving ? "Saving..." : patchNoteFormMode === "edit" ? "Update" : "Create"}
                    </Button>
                  </Stack>
                </Box>
              </Grid>

              <Grid item xs={12} md={7}>
                <Typography variant="subtitle1" gutterBottom>
                  Existing Announcements
                </Typography>

                {patchNotesLoading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress size={28} />
                  </Box>
                ) : patchNotes.length === 0 ? (
                  <Alert severity="info">No announcements yet. Create one to get started.</Alert>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Title</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Version</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Created</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {patchNotes.map((note) => (
                          <TableRow key={note.id} hover>
                            <TableCell>
                              <Typography variant="subtitle2">{note.title || "(No title)"}</Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {note.content || "No content"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={note.type || "update"}
                                size="small"
                                color={
                                  note.type === "alert" ? "error" :
                                  note.type === "patch" ? "warning" :
                                  note.type === "info" ? "info" : "success"
                                }
                              />
                            </TableCell>
                            <TableCell>{note.version || "—"}</TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={note.isActive ? "Active" : "Inactive"}
                                color={note.isActive ? "success" : "default"}
                              />
                            </TableCell>
                            <TableCell>{formatTimestamp(note.createdAt)}</TableCell>
                            <TableCell align="right">
                              <Stack direction="row" spacing={1} justifyContent="flex-end">
                                <Button size="small" variant="outlined" onClick={() => handleEditPatchNote(note)}>
                                  Edit
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => handleTogglePatchNoteActive(note)}
                                >
                                  {note.isActive ? "Deactivate" : "Activate"}
                                </Button>
                                <IconButton size="small" color="error" onClick={() => handleDeletePatchNote(note.id)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Resource Management */}
        <Grid item xs={12}>
          <Paper elevation={1} sx={{ p: 3 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              sx={{ mb: 2 }}
            >
              <Box>
                <Typography variant="h5">Resource Management</Typography>
                <Typography variant="body2" color="text.secondary">
                  Manage Quick Links, Policies, and ICS-205 content shown on the My Vault Resources page.
                </Typography>
              </Box>
              <Stack direction="row" spacing={1}>
                <TextField
                  select
                  size="small"
                  value={resourceTypeFilter}
                  onChange={(e) => setResourceTypeFilter(e.target.value)}
                  sx={{ minWidth: 140 }}
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="quick-link">Quick Links</MenuItem>
                  <MenuItem value="policy">Policies</MenuItem>
                  <MenuItem value="ics-205">ICS-205</MenuItem>
                </TextField>
                <Button variant="outlined" onClick={fetchResources} disabled={resourcesLoading}>
                  {resourcesLoading ? "Refreshing..." : "Refresh"}
                </Button>
              </Stack>
            </Stack>

            {resourcesAlert && (
              <Alert severity={resourcesAlert.severity} sx={{ mb: 2 }} onClose={() => setResourcesAlert(null)}>
                {resourcesAlert.message}
              </Alert>
            )}

            <Grid container spacing={3}>
              <Grid item xs={12} md={5}>
                <Typography variant="subtitle1" gutterBottom>
                  {resourceFormMode === "edit" ? "Edit Resource" : "Create Resource"}
                </Typography>
                <Box component="form" onSubmit={handleResourceSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <TextField
                    select
                    label="Type"
                    value={resourceForm.type}
                    onChange={handleResourceFormChange("type")}
                    required
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="quick-link">Quick Link</MenuItem>
                    <MenuItem value="policy">Policy</MenuItem>
                    <MenuItem value="ics-205">ICS-205</MenuItem>
                  </TextField>
                  <TextField
                    label="Title"
                    value={resourceForm.title}
                    onChange={handleResourceFormChange("title")}
                    required
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="Description"
                    value={resourceForm.description}
                    onChange={handleResourceFormChange("description")}
                    fullWidth
                    size="small"
                    multiline
                    rows={2}
                  />
                  {resourceForm.type === "quick-link" && (
                    <TextField
                      label="URL"
                      value={resourceForm.url}
                      onChange={handleResourceFormChange("url")}
                      required
                      fullWidth
                      size="small"
                      placeholder="https://example.com"
                    />
                  )}
                  {resourceForm.type === "quick-link" && (
                    <TextField
                      select
                      label="Category"
                      value={resourceForm.category}
                      onChange={handleResourceFormChange("category")}
                      required
                      fullWidth
                      size="small"
                      helperText="Which section this link appears under on the Resources page"
                    >
                      <MenuItem value="common">Common (shown to all)</MenuItem>
                      <MenuItem value="texas">Texas</MenuItem>
                      <MenuItem value="south-carolina">South Carolina</MenuItem>
                    </TextField>
                  )}
                  {resourceForm.type === "policy" && (
                    <>
                      <TextField
                        label="Content (Markdown supported)"
                        value={resourceForm.content}
                        onChange={handleResourceFormChange("content")}
                        fullWidth
                        size="small"
                        multiline
                        rows={10}
                        placeholder={`# Main Title

## Section Header

### Subsection

Regular paragraph text goes here. Use **bold text** for emphasis.

- Bullet point 1
- Bullet point 2
- Bullet point 3

1. Numbered item
2. Another item

> This is a blockquote for important notes

---

Use horizontal rules to separate sections.`}
                      />
                      <Box sx={{ p: 1.5, backgroundColor: 'action.hover', borderRadius: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>Formatting Guide:</Typography>
                        <Typography variant="caption" component="div" sx={{ mt: 0.5, fontFamily: 'monospace', fontSize: '0.7rem' }}>
                          # H1 Header &nbsp;|&nbsp; ## H2 Header &nbsp;|&nbsp; ### H3 Header<br/>
                          **bold text** &nbsp;|&nbsp; *italic text*<br/>
                          - Bullet point &nbsp;|&nbsp; 1. Numbered list<br/>
                          {"> Blockquote"} &nbsp;|&nbsp; --- Horizontal rule
                        </Typography>
                      </Box>
                    </>
                  )}
                  {resourceForm.type === "ics-205" && (
                    <Alert severity="info">
                      Use the ICS-205 Editor section below to edit the radio communications plan.
                    </Alert>
                  )}
                  <TextField
                    label="Sort Order"
                    type="number"
                    value={resourceForm.sortOrder}
                    onChange={handleResourceFormChange("sortOrder")}
                    fullWidth
                    size="small"
                    helperText="Lower numbers appear first"
                  />
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <input
                      type="checkbox"
                      id="resourceIsActive"
                      checked={resourceForm.isActive}
                      onChange={handleResourceFormChange("isActive")}
                    />
                    <label htmlFor="resourceIsActive">
                      <Typography variant="body2">Active (visible to users)</Typography>
                    </label>
                  </Stack>
                  <Stack direction="row" justifyContent="flex-end" spacing={1}>
                    {resourceFormMode === "edit" && (
                      <Button type="button" variant="text" onClick={resetResourceForm}>
                        Cancel
                      </Button>
                    )}
                    <Button type="submit" variant="contained" disabled={resourceSaving}>
                      {resourceSaving ? "Saving..." : resourceFormMode === "edit" ? "Update" : "Create"}
                    </Button>
                  </Stack>
                </Box>
              </Grid>

              <Grid item xs={12} md={7}>
                <Typography variant="subtitle1" gutterBottom>
                  Existing Resources ({filteredResources.length})
                </Typography>

                {resourcesLoading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress size={28} />
                  </Box>
                ) : filteredResources.length === 0 ? (
                  <Alert severity="info">No resources found. Create one to get started.</Alert>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Title</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Category</TableCell>
                          <TableCell>Order</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredResources.map((resource) => (
                          <TableRow key={resource.id} hover>
                            <TableCell>
                              <Typography variant="subtitle2">{resource.title || "(No title)"}</Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {resource.description || (resource.type === "quick-link" ? resource.url : "No description")}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={resource.type === "quick-link" ? "Link" : resource.type === "policy" ? "Policy" : "ICS-205"}
                                size="small"
                                color={resource.type === "quick-link" ? "info" : resource.type === "policy" ? "warning" : "success"}
                              />
                            </TableCell>
                            <TableCell>
                              {resource.type === "quick-link" ? (
                                <Chip
                                  label={resource.category === "texas" ? "Texas" : resource.category === "south-carolina" ? "South Carolina" : "Common"}
                                  size="small"
                                  variant="outlined"
                                  color={resource.category === "texas" ? "warning" : resource.category === "south-carolina" ? "secondary" : "default"}
                                />
                              ) : (
                                <Typography variant="body2" color="text.secondary">—</Typography>
                              )}
                            </TableCell>
                            <TableCell>{resource.sortOrder}</TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={resource.isActive ? "Active" : "Hidden"}
                                color={resource.isActive ? "success" : "default"}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Stack direction="row" spacing={1} justifyContent="flex-end">
                                <Button size="small" variant="outlined" onClick={() => handleEditResource(resource)}>
                                  Edit
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => handleToggleResourceActive(resource)}
                                >
                                  {resource.isActive ? "Hide" : "Show"}
                                </Button>
                                <IconButton size="small" color="error" onClick={() => handleDeleteResource(resource.id)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* ICS-205 Editor */}
        <Grid item xs={12}>
          <Paper elevation={1} sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>ICS-205 Radio Communications Plan Editor</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Edit the ICS-205 form displayed on the My Vault Resources page. Changes are saved to the database.
            </Typography>

            {resourcesAlert && resourcesAlert.message.includes("ICS-205") && (
              <Alert severity={resourcesAlert.severity} sx={{ mb: 2 }} onClose={() => setResourcesAlert(null)}>
                {resourcesAlert.message}
              </Alert>
            )}

            <Grid container spacing={3}>
              {/* Basic Info */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>Incident Information</Typography>
                <Stack spacing={2}>
                  <TextField
                    label="Incident Name"
                    value={ics205Form.incidentName}
                    onChange={(e) => handleIcs205FieldChange("incidentName", e.target.value)}
                    fullWidth
                    size="small"
                  />
                  <Typography variant="subtitle2">Operational Period</Typography>
                  <Stack direction="row" spacing={1}>
                    <TextField
                      label="Date From"
                      value={ics205Form.operationalPeriod?.dateFrom || ""}
                      onChange={(e) => handleIcs205OperationalPeriodChange("dateFrom", e.target.value)}
                      size="small"
                      fullWidth
                    />
                    <TextField
                      label="Date To"
                      value={ics205Form.operationalPeriod?.dateTo || ""}
                      onChange={(e) => handleIcs205OperationalPeriodChange("dateTo", e.target.value)}
                      size="small"
                      fullWidth
                    />
                  </Stack>
                  <Stack direction="row" spacing={1}>
                    <TextField
                      label="Time From"
                      value={ics205Form.operationalPeriod?.timeFrom || ""}
                      onChange={(e) => handleIcs205OperationalPeriodChange("timeFrom", e.target.value)}
                      size="small"
                      placeholder="0600"
                      fullWidth
                    />
                    <TextField
                      label="Time To"
                      value={ics205Form.operationalPeriod?.timeTo || ""}
                      onChange={(e) => handleIcs205OperationalPeriodChange("timeTo", e.target.value)}
                      size="small"
                      placeholder="1800"
                      fullWidth
                    />
                  </Stack>
                </Stack>
              </Grid>

              {/* Prepared By */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>Prepared By</Typography>
                <Stack spacing={2}>
                  <TextField
                    label="Name"
                    value={ics205Form.preparedBy?.name || ""}
                    onChange={(e) => handleIcs205PreparedByChange("name", e.target.value)}
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="Position"
                    value={ics205Form.preparedBy?.position || ""}
                    onChange={(e) => handleIcs205PreparedByChange("position", e.target.value)}
                    fullWidth
                    size="small"
                  />
                  <Stack direction="row" spacing={1}>
                    <TextField
                      label="Date"
                      value={ics205Form.preparedBy?.date || ""}
                      onChange={(e) => handleIcs205PreparedByChange("date", e.target.value)}
                      size="small"
                      fullWidth
                    />
                    <TextField
                      label="Time"
                      value={ics205Form.preparedBy?.time || ""}
                      onChange={(e) => handleIcs205PreparedByChange("time", e.target.value)}
                      size="small"
                      fullWidth
                    />
                  </Stack>
                </Stack>
              </Grid>

              {/* Radio Channels */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="subtitle1">Radio Channels ({ics205Form.basicRadioChannelUse?.length || 0})</Typography>
                  <Button variant="outlined" size="small" onClick={handleAddIcs205Channel}>
                    + Add Channel
                  </Button>
                </Stack>
                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ minWidth: 80 }}>Zone</TableCell>
                        <TableCell sx={{ minWidth: 100 }}>Channel</TableCell>
                        <TableCell sx={{ minWidth: 80 }}>Function</TableCell>
                        <TableCell sx={{ minWidth: 100 }}>Assignment</TableCell>
                        <TableCell sx={{ minWidth: 90 }}>RX Freq</TableCell>
                        <TableCell sx={{ minWidth: 70 }}>RX Tone</TableCell>
                        <TableCell sx={{ minWidth: 90 }}>TX Freq</TableCell>
                        <TableCell sx={{ minWidth: 70 }}>TX Tone</TableCell>
                        <TableCell sx={{ minWidth: 60 }}>Mode</TableCell>
                        <TableCell sx={{ minWidth: 100 }}>Remarks</TableCell>
                        <TableCell sx={{ width: 50 }}></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(ics205Form.basicRadioChannelUse || []).map((channel, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <TextField
                              value={channel.zone || ""}
                              onChange={(e) => handleIcs205ChannelChange(index, "zone", e.target.value)}
                              size="small"
                              variant="standard"
                              fullWidth
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              value={channel.channel || ""}
                              onChange={(e) => handleIcs205ChannelChange(index, "channel", e.target.value)}
                              size="small"
                              variant="standard"
                              fullWidth
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              value={channel.function || ""}
                              onChange={(e) => handleIcs205ChannelChange(index, "function", e.target.value)}
                              size="small"
                              variant="standard"
                              fullWidth
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              value={channel.assignment || ""}
                              onChange={(e) => handleIcs205ChannelChange(index, "assignment", e.target.value)}
                              size="small"
                              variant="standard"
                              fullWidth
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              value={channel.rxFreq || ""}
                              onChange={(e) => handleIcs205ChannelChange(index, "rxFreq", e.target.value)}
                              size="small"
                              variant="standard"
                              fullWidth
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              value={channel.rxTone || ""}
                              onChange={(e) => handleIcs205ChannelChange(index, "rxTone", e.target.value)}
                              size="small"
                              variant="standard"
                              fullWidth
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              value={channel.txFreq || ""}
                              onChange={(e) => handleIcs205ChannelChange(index, "txFreq", e.target.value)}
                              size="small"
                              variant="standard"
                              fullWidth
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              value={channel.txTone || ""}
                              onChange={(e) => handleIcs205ChannelChange(index, "txTone", e.target.value)}
                              size="small"
                              variant="standard"
                              fullWidth
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              select
                              value={channel.mode || "A"}
                              onChange={(e) => handleIcs205ChannelChange(index, "mode", e.target.value)}
                              size="small"
                              variant="standard"
                              fullWidth
                            >
                              <MenuItem value="A">A</MenuItem>
                              <MenuItem value="D">D</MenuItem>
                              <MenuItem value="M">M</MenuItem>
                            </TextField>
                          </TableCell>
                          <TableCell>
                            <TextField
                              value={channel.remarks || ""}
                              onChange={(e) => handleIcs205ChannelChange(index, "remarks", e.target.value)}
                              size="small"
                              variant="standard"
                              fullWidth
                            />
                          </TableCell>
                          <TableCell>
                            <IconButton size="small" color="error" onClick={() => handleRemoveIcs205Channel(index)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>

              {/* Special Instructions */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="subtitle1">Special Instructions ({ics205Form.specialInstructions?.length || 0})</Typography>
                  <Button variant="outlined" size="small" onClick={handleAddIcs205Instruction}>
                    + Add Instruction
                  </Button>
                </Stack>
                <Stack spacing={1}>
                  {(ics205Form.specialInstructions || []).map((instruction, index) => (
                    <Stack key={index} direction="row" spacing={1} alignItems="center">
                      <TextField
                        value={instruction}
                        onChange={(e) => handleIcs205InstructionChange(index, e.target.value)}
                        fullWidth
                        size="small"
                        placeholder={`Instruction ${index + 1}`}
                      />
                      <IconButton size="small" color="error" onClick={() => handleRemoveIcs205Instruction(index)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  ))}
                </Stack>
              </Grid>

              {/* Save Button */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Stack direction="row" justifyContent="flex-end">
                  <Button
                    variant="contained"
                    onClick={handleSaveIcs205}
                    disabled={resourceSaving}
                    size="large"
                  >
                    {resourceSaving ? "Saving..." : ics205ResourceId ? "Update ICS-205" : "Create ICS-205"}
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={Boolean(deleteTarget)} onClose={closeDeleteDialog} fullWidth maxWidth="xs">
        <DialogTitle>Delete Account</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the account "{deleteTarget?.username}"? This action cannot be undone.
          </DialogContentText>
          {deleteError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {deleteError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button onClick={handleDeleteAccount} color="error" variant="contained" disabled={deleteLoading}>
            {deleteLoading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(editTarget)} onClose={closeEditDialog} fullWidth maxWidth="sm">
        <DialogTitle>Edit Account</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Update profile details for "{editTarget?.username}". Leave a field unchanged if you do not need to modify it.
          </DialogContentText>
          {editError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {editError}
            </Alert>
          )}
          <Box component="form" onSubmit={handleEditSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField label="Name" value={editForm.name} onChange={handleEditFieldChange("name")} fullWidth />
            <TextField label="Email" type="email" value={editForm.email} onChange={handleEditFieldChange("email")} fullWidth />
            <TextField label="Phone Number" value={editForm.phoneNumber} onChange={handleEditFieldChange("phoneNumber")} fullWidth />
            <TextField label="Ranger ID" value={editForm.rangerId} onChange={handleEditFieldChange("rangerId")} fullWidth />
            <TextField label="Division" value={editForm.division} onChange={handleEditFieldChange("division")} fullWidth />
            <FormControlLabel
              control={
                <Checkbox
                  checked={editForm.canViewComms}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, canViewComms: e.target.checked }))}
                />
              }
              label="View Comms (ICS-205 / Radio)"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={editForm.incidentCreationRestricted}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, incidentCreationRestricted: e.target.checked }))}
                />
              }
              label="Restrict Incident Creation (Core Classes Required)"
            />
            <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ mt: 1 }}>
              <Button type="button" onClick={closeEditDialog} disabled={editSaving}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" disabled={editSaving}>
                {editSaving ? "Saving..." : "Save"}
              </Button>
            </Stack>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Reject Certification Dialog */}
      <Dialog open={rejectDialog.open} onClose={() => setRejectDialog({ open: false, certId: null, notes: "" })} fullWidth maxWidth="sm">
        <DialogTitle>Reject Certification</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Provide optional notes explaining why this certification is being rejected. The ranger will see these notes.
          </DialogContentText>
          <TextField
            label="Rejection Notes (optional)"
            multiline
            rows={3}
            fullWidth
            value={rejectDialog.notes}
            onChange={(e) => setRejectDialog((prev) => ({ ...prev, notes: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog({ open: false, certId: null, notes: "" })}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleRejectCert} disabled={reviewLoading === rejectDialog.certId}>
            {reviewLoading === rejectDialog.certId ? <CircularProgress size={18} /> : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default AdminPanel;
